// MyErrorBoundary.tsx
import { useEffect } from "react";
import { useNavigate, useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

// If you're using the official New Relic Browser agent, you can call `window.newrelic.noticeError(...)`
declare global {
    interface Window {
        newrelic?: {
            noticeError: (err: any) => void;
        };
    }
}

export default function MyErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();

    // Report to New Relic on mount
    useEffect(() => {
        if (error) {
            window.newrelic?.noticeError?.(error);
        }
    }, [error]);

    if (isRouteErrorResponse(error)) {
        // This is a special error from a loader/action in React Router
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10">
                <ExclamationTriangleIcon className="text-red-500 w-16 h-16 mb-4" />
                <h1 className="text-3xl font-semibold text-red-600 mb-2">
                    Oops! A loading error occurred.
                </h1>
                <p className="text-gray-700 mb-4">
                    {error.status} {error.statusText}
                </p>
                <p className="text-gray-500 mb-8">
                    Please try again or contact support.
                </p>
                <div className="flex space-x-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                    >
                        Go Back
                    </button>
                    <Link
                        to="/"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                        Return Home
                    </Link>
                </div>
            </div>
        );
    } else if (error instanceof Error) {
        // A normal JS error
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10">
                <ExclamationTriangleIcon className="text-red-500 w-16 h-16 mb-4" />
                <h1 className="text-3xl font-semibold text-red-600 mb-2">
                    Something went wrong!
                </h1>
                <p className="text-gray-700 mb-4">We apologize for the inconvenience.</p>

                {/*
          For advanced debugging, you can show the error details:

          <details className="bg-gray-100 p-3 rounded mt-2 text-sm text-gray-700">
            <summary className="cursor-pointer">Error details</summary>
            <p>{error.message}</p>
            <pre className="mt-2 whitespace-pre-wrap">{error.stack}</pre>
          </details>
        */}

                <div className="flex space-x-2 mt-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full"
                    >
                        Go Back
                    </button>
                    <Link
                        to="/"
                        className="px-4 py-2 bg-bg-fairway hover:bg-bg-fairway-hover text-white rounded-full"
                    >
                        Return Home
                    </Link>
                </div>
            </div>
        );
    } else {
        // Unknown error
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10">
                <ExclamationTriangleIcon className="text-red-500 w-16 h-16 mb-4" />
                <h1 className="text-3xl font-semibold text-red-600 mb-2">
                    Unknown error
                </h1>
                <p className="text-gray-700 mb-4">
                    Please try again or contact support if the problem persists.
                </p>
                <Link
                    to="/"
                    className="px-4 py-2 bg-bg-fairway hover:bg-bg-fairway-hover text-white rounded-full"
                >
                    Return Home
                </Link>
            </div>
        );
    }
}